// Geo-targeted banners module for Principle theme
// This module handles fetching visitor country data and banner configurations
// and integrates with the theme's AJAX navigation system

// Configuration
const CONFIG = {
  ipifyUrl: 'https://api64.ipify.org?format=json',
  ipinfoToken: '77be97e23fcd9e', // Replace with your ipinfo.io token
  googleSheetId: '12DTe4U2Qg0IIk5NGCGasSOaOmRtS-tlJWuZrqEv-a-0', // Replace with your Google Sheet ID
  googleApiKey: 'AIzaSyAx7ZIe_HchI8Dl5aTBv4BzwhHaW2kndtI', // Replace with your Google Sheets API key
  cacheKey: 'geoTargetedBannerCache',
  cacheExpiry: 24 * 60 * 60 * 1000, // 24 hours
  countryCacheExpiry: 3 * 24 * 60 * 60 * 1000, // 3 days
  defaultCountry: 'default'
};

// Class to handle geo-targeted banners
export class GeoTargetedBanners {
  constructor() {
    this.visitorCountry = null;
    this.bannerData = [];
    this.isInitialized = false;
    this.initPromise = null;
  }

  // Initialize the module
  async init() {
    // Only initialize once and return the same promise for concurrent calls
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise(async (resolve) => {
      try {
        // Try to get visitor country from cache first
        const cachedCountryData = localStorage.getItem('visitorCountry');
        let needsFreshCountry = true;
        
        if (cachedCountryData) {
          try {
            // Check if the cached data is in the new format with timestamp
            if (cachedCountryData.includes('{')) {
              const { country, timestamp } = JSON.parse(cachedCountryData);
              
              // Check if the cache is still valid (less than 3 days old)
              if (Date.now() - timestamp < CONFIG.countryCacheExpiry) {
                this.visitorCountry = country;
                needsFreshCountry = false;
              }
            } else {
              // Handle legacy format (just the country string)
              this.visitorCountry = cachedCountryData;
              
              // Update to new format with timestamp
              localStorage.setItem('visitorCountry', JSON.stringify({
                country: this.visitorCountry,
                timestamp: Date.now()
              }));
              
              needsFreshCountry = false;
            }
          } catch (error) {
            console.error('Error parsing cached country data:', error);
            // Will fetch fresh data below
          }
        }
        
        if (needsFreshCountry) {
          // Fetch visitor's IP and country
          const ip = await this.getVisitorIP();
          this.visitorCountry = await this.getVisitorCountry(ip);
          
          // Cache the country with timestamp
          localStorage.setItem('visitorCountry', JSON.stringify({
            country: this.visitorCountry,
            timestamp: Date.now()
          }));
        }

        // Fetch banner data
        this.bannerData = await this.getCachedBannerData();
        
        // Set the global bannersData variable that the theme uses
        window.bannersData = this.bannerData;
        
        this.isInitialized = true;
        console.log(`Geo-targeted banners initialized. Visitor country: ${this.visitorCountry}`);
        
        resolve(true);
      } catch (error) {
        console.error('Error initializing geo-targeted banners:', error);
        // Set default values in case of error
        this.visitorCountry = CONFIG.defaultCountry;
        window.bannersData = [];
        this.isInitialized = true;
        resolve(false);
      }
    });

    return this.initPromise;
  }

  // Fetch visitor's IP address
  async getVisitorIP() {
    try {
      const response = await fetch(CONFIG.ipifyUrl);
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error fetching IP address:', error);
      throw error;
    }
  }

  // Fetch visitor's country using ipinfo.io
  async getVisitorCountry(ip) {
    try {
      const response = await fetch(`https://api.ipinfo.io/lite/${ip}?token=${CONFIG.ipinfoToken}`);
      const data = await response.json();
      return data.country_code || CONFIG.defaultCountry;
    } catch (error) {
      console.error('Error fetching country:', error);
      return CONFIG.defaultCountry;
    }
  }

  // Fetch and cache banner data using Google Sheets API
  async getCachedBannerData() {
    try {
      let shouldFetchFresh = false;
      let cachedData = null;
      
      // Check if cached data exists
      const cachedBannerData = localStorage.getItem(CONFIG.cacheKey);
      if (cachedBannerData) {
        try {
          const { data, timestamp } = JSON.parse(cachedBannerData);
          cachedData = data;
          
          // Check if cache is stale
          if (Date.now() - timestamp >= CONFIG.cacheExpiry) {
            shouldFetchFresh = true;
          }
        } catch (error) {
          console.error('Error parsing cached banner data:', error);
          shouldFetchFresh = true;
        }
      } else {
        shouldFetchFresh = true;
      }
      
      // If we have cached data, use it immediately
      if (cachedData) {
        // If cache is stale, fetch fresh data in the background
        if (shouldFetchFresh) {
          // Use setTimeout to make this non-blocking
          setTimeout(() => {
            this.fetchFreshBannerData()
              .then(freshData => {
                // Update the global bannersData variable with fresh data
                window.bannersData = this.filterBannersByCountry(freshData);
              })
              .catch(error => {
                console.error('Background refresh error:', error);
              });
          }, 0);
        }
        return this.filterBannersByCountry(cachedData);
      }
      
      // No cache available, must wait for fresh data
      return await this.fetchFreshBannerData();
    } catch (error) {
      console.error('Error in getCachedBannerData:', error);
      return [];
    }
  }
  
  // Separate method to fetch fresh banner data
  async fetchFreshBannerData() {
    try {
      // Fetch fresh data from Google Sheets API
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${CONFIG.googleSheetId}/values/Sheet1?key=${CONFIG.googleApiKey}`
      );
      
      if (!response.ok) {
        throw new Error(`Google Sheets API error: ${response.status}`);
      }
      
      const jsonData = await response.json();
      
      if (!jsonData.values || jsonData.values.length <= 1) {
        throw new Error('No data found in Google Sheet');
      }
      
      const rows = jsonData.values.slice(1); // Skip header row
      
      const data = rows.map(row => {
        const [postSlug, visitorCountry, image, text, link] = row;
        return { 
          postSlug: postSlug.startsWith('/') ? postSlug : `/${postSlug}`, // Ensure slug starts with /
          visitorCountry, 
          image, 
          text, 
          link 
        };
      });

      // Cache the data
      localStorage.setItem(CONFIG.cacheKey, JSON.stringify({ 
        data, 
        timestamp: Date.now() 
      }));

      return data;
    } catch (error) {
      console.error('Error fetching fresh banner data:', error);
      throw error; // Re-throw to be handled by the caller
    }
  }

  // Filter banners by visitor country
  filterBannersByCountry(banners) {
    if (!this.visitorCountry) {
      return banners.filter(banner => banner.visitorCountry === CONFIG.defaultCountry);
    }

    // First try to find banners specific to the visitor's country
    const countrySpecificBanners = banners.filter(
      banner => banner.visitorCountry === this.visitorCountry
    );

    // If no country-specific banners found, fall back to default banners
    if (countrySpecificBanners.length === 0) {
      return banners.filter(banner => banner.visitorCountry === CONFIG.defaultCountry);
    }

    return countrySpecificBanners;
  }

  // Update banners after navigation
  updateBanners() {
    if (!this.isInitialized) {
      this.init().then(() => {
        this.applyBanners();
      });
    } else {
      this.applyBanners();
    }
  }

  // Apply banners to the current page
  applyBanners() {
    const content = document.querySelector('.post');
    const sidebar = document.querySelector('.sidebar');
    const mobileAuthorsSection = document.querySelector('.sidebar__authors');
    
    // Apply banners to the main sidebar (desktop view)
    if (content && sidebar) {
      // Use the theme's existing function if available
      if (typeof changeSidebarBanner === 'function') {
        changeSidebarBanner(content, sidebar);
      } else {
        // Fallback implementation if the theme's function is not available
        this.changeSidebarBanner(content, sidebar);
      }
    }
    
    // Apply banners to the mobile view (sidebar__authors section)
    if (content && mobileAuthorsSection) {
      const mobileBanner = mobileAuthorsSection.querySelector('.sidebar-adv-banner__banner');
      const mobileBannerImgWrapper = mobileAuthorsSection.querySelector('.sidebar-adv-banner__img-wrapper');
      const mobileBannerText = mobileAuthorsSection.querySelector('.sidebar-adv-banner__text');
      
      if (mobileBanner && mobileBannerImgWrapper && mobileBannerText) {
        const currentPathName = window.location.pathname;
        
        if (window.bannersData && window.bannersData.length > 0) {
          window.bannersData.forEach((bannerData) => {
            if (currentPathName === bannerData.postSlug) {
              // Update link if provided
              if (bannerData.link) {
                if (mobileBanner.tagName !== 'A') {
                  const linkElement = document.createElement('a');
                  linkElement.href = bannerData.link;
                  linkElement.classList.add(
                    'sidebar-adv-banner__banner',
                    'sidebar-adv-banner__banner--link'
                  );

                  while (mobileBanner.firstChild) {
                    linkElement.appendChild(mobileBanner.firstChild);
                  }

                  mobileBanner.parentNode.replaceChild(linkElement, mobileBanner);
                } else {
                  mobileBanner.href = bannerData.link;
                }
              } else {
                if (mobileBanner.tagName === 'A') {
                  const divElement = document.createElement('div');
                  divElement.classList.add('sidebar-adv-banner__banner');

                  while (mobileBanner.firstChild) {
                    divElement.appendChild(mobileBanner.firstChild);
                  }

                  mobileBanner.parentNode.replaceChild(divElement, mobileBanner);
                }
              }

              // Update text if provided
              if (bannerData.text) {
                mobileBannerText.textContent = bannerData.text;
                mobileBannerImgWrapper.textContent = '';
              }

              // Update image if provided
              if (bannerData.image) {
                const img = document.createElement('img');
                img.classList.add('sidebar-adv-banner__img');
                img.src = bannerData.image;
                img.alt = 'banner image';

                mobileBannerImgWrapper.appendChild(img);
              }
            }
          });
        }
      }
    }
  }

  // Fallback implementation of changeSidebarBanner
  changeSidebarBanner(content, sidebar) {
    if (!window.bannersData || window.bannersData.length === 0) {
      return;
    }

    const banner = sidebar.querySelector('.sidebar-adv-banner__banner');
    const bannerImgWrapper = sidebar.querySelector('.sidebar-adv-banner__img-wrapper');
    const bannerText = sidebar.querySelector('.sidebar-adv-banner__text');
    
    if (!banner || !bannerImgWrapper || !bannerText) {
      return;
    }
    
    const currentPathName = window.location.pathname;

    if (content.classList.contains('post')) {
      window.bannersData.forEach((bannerData) => {
        if (currentPathName === bannerData.postSlug) {
          if (bannerData.link) {
            if (banner.tagName !== 'A') {
              const linkElement = document.createElement('a');
              linkElement.href = bannerData.link;
              linkElement.classList.add(
                'sidebar-adv-banner__banner',
                'sidebar-adv-banner__banner--link'
              );

              while (banner.firstChild) {
                linkElement.appendChild(banner.firstChild);
              }

              banner.parentNode.replaceChild(linkElement, banner);
            } else {
              banner.href = bannerData.link;
            }
          } else {
            if (banner.tagName === 'A') {
              const divElement = document.createElement('div');
              divElement.classList.add('sidebar-adv-banner__banner');

              while (banner.firstChild) {
                divElement.appendChild(banner.firstChild);
              }

              banner.parentNode.replaceChild(divElement, banner);
            }
          }

          if (bannerData.text) {
            bannerText.textContent = bannerData.text;
            bannerImgWrapper.textContent = '';
          }

          if (bannerData.image) {
            const img = document.createElement('img');
            img.classList.add('sidebar-adv-banner__img');
            img.src = bannerData.image;
            img.alt = 'banner image';

            bannerImgWrapper.appendChild(img);
          }
        }
      });
    }
  }
}

// Create and export a singleton instance
export const geoTargetedBanners = new GeoTargetedBanners();

// Initialize the module
document.addEventListener('DOMContentLoaded', () => {
  geoTargetedBanners.init().then(() => {
    // Apply banners after initialization
    geoTargetedBanners.updateBanners();
    
    // Listen for AJAX navigation events
    window.addEventListener('popstate', () => {
      setTimeout(() => geoTargetedBanners.updateBanners(), 1000);
    });
  });
});
